import { useEffect, useState } from "react";
import type { ExecutionStatus } from "../../lib/types.js";
import { ThemeToggle } from "../ThemeToggle.js";

interface MonitorToolbarProps {
	workflowName: string;
	executionStatus: ExecutionStatus;
	elapsed: number; // seconds
	onCancel: () => void;
	onStop: () => void;
}

const STATUS_COLORS: Record<ExecutionStatus, string> = {
	pending: "var(--forge-color-text-muted)",
	running: "var(--forge-color-accent)",
	completed: "var(--forge-color-success)",
	failed: "var(--forge-color-error)",
	cancelled: "var(--forge-color-text-muted)",
};

const TERMINAL_STATUSES: ExecutionStatus[] = ["completed", "failed", "cancelled"];

function formatElapsed(seconds: number): string {
	const m = Math.floor(seconds / 60);
	const s = seconds % 60;
	return `${m}:${String(s).padStart(2, "0")}`;
}

export function MonitorToolbar({
	workflowName,
	executionStatus,
	elapsed,
	onCancel,
	onStop,
}: MonitorToolbarProps) {
	const [confirming, setConfirming] = useState(false);

	useEffect(() => {
		if (!confirming) return;
		const timer = setTimeout(() => setConfirming(false), 3000);
		return () => clearTimeout(timer);
	}, [confirming]);

	const isTerminal = TERMINAL_STATUSES.includes(executionStatus);
	const dotColor = STATUS_COLORS[executionStatus];
	const isRunning = executionStatus === "running";

	function handleCancelClick() {
		if (confirming) {
			onCancel();
			setConfirming(false);
		} else {
			setConfirming(true);
		}
	}

	const btnBase =
		"rounded-xl border border-border bg-surface px-4 py-2 text-sm text-text-muted transition-colors duration-100 hover:text-text-primary";

	return (
		<div className="absolute left-3 right-3 top-3 z-10 flex items-center justify-between">
			<div className="flex items-center gap-3">
				<span className="text-sm font-medium text-text-primary">{workflowName}</span>
				<div className="flex items-center gap-1.5">
					<span
						style={{
							backgroundColor: dotColor,
							width: 8,
							height: 8,
							borderRadius: "50%",
							display: "inline-block",
						}}
						className={isRunning ? "animate-pulse" : undefined}
						aria-hidden="true"
					/>
					<span className="text-xs text-text-muted">{executionStatus}</span>
				</div>
				<span className="text-xs text-text-muted">{formatElapsed(elapsed)}</span>
			</div>
			<div className="flex items-center gap-2">
				{!isTerminal && (
					<button type="button" onClick={handleCancelClick} className={btnBase}>
						{confirming ? "Confirm Cancel" : "Cancel Execution"}
					</button>
				)}
				<button type="button" onClick={onStop} className={btnBase}>
					Stop Monitoring
				</button>
				<ThemeToggle />
			</div>
		</div>
	);
}
